/**
 * Cards layout variants (NOS-152). `grid` remains the original CardsBlock
 * layout (untouched default). Variants share the block's content fields —
 * switching never loses content — and are token-driven via the --nos-*
 * variables.
 *
 * Note: per-item `_styles` is a grid-layout feature; variants define their own
 * composition and intentionally ignore it (same convention as hero variants).
 */
import { HEADING_COLOR, type BlockComponentProps } from "./shared"
import { sanitizeHtml, safeHref } from "@/lib/sanitizeHtml"

interface CardItem {
  title?: string
  body?: string
  image_url?: string
  cta_text?: string
  cta_url?: string
  visible?: boolean
}

function parseCardsContent(content: Record<string, unknown> | null) {
  const heading = (content?.heading as string) ?? ""
  const subheading = (content?.subheading as string) ?? ""
  const items = ((content?.items as CardItem[]) ?? []).filter((item) => item.visible !== false)
  const columns = Math.min(3, Math.max(2, (content?.columns as number) ?? 2))
  return { heading, subheading, items, columns }
}

const GRADIENT = "linear-gradient(135deg, var(--nos-color-primary, #3b82f6) 0%, var(--nos-color-accent, var(--nos-color-secondary, #1e40af)) 100%)"

function CardsHeader({ heading, subheading }: { heading: string; subheading: string }) {
  if (!heading && !subheading) return null
  return (
    <>
      {heading && (
        <h2 className="text-center text-2xl font-bold" style={{ color: HEADING_COLOR }}>
          {heading}
        </h2>
      )}
      {subheading && (
        <p className="mt-1 text-center" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
          {subheading}
        </p>
      )}
    </>
  )
}

/** Feature list: numbered vertical list — no cards, no borders, no images. */
export function CardsFeatureList({ content }: BlockComponentProps) {
  const { heading, subheading, items } = parseCardsContent(content)
  if (items.length === 0) return null
  return (
    <section className="px-4 @sm:px-6 @md:px-8" style={{ paddingTop: "calc(var(--nos-space-section-y, 64px) * 0.75)", paddingBottom: "calc(var(--nos-space-section-y, 64px) * 0.75)" }}>
      <CardsHeader heading={heading} subheading={subheading} />
      <div className="mx-auto mt-10 max-w-3xl space-y-8">
        {items.map((card, i) => (
          <div key={i} className="flex items-start gap-5">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-base font-bold"
              style={{
                background: "color-mix(in srgb, var(--nos-color-primary, #3b82f6) 12.5%, transparent)",
                color: "var(--nos-color-primary, #3b82f6)",
              }}
              aria-hidden
            >
              {i + 1}
            </div>
            <div className="min-w-0">
              {card.title && (
                <h3 className="text-lg font-semibold" style={{ color: "var(--nos-color-text, #0f172a)" }}>
                  {card.title}
                </h3>
              )}
              {card.body && (
                <p
                  className="mt-1 leading-relaxed"
                  style={{ color: "var(--nos-color-text-muted, #64748b)" }}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.body) }}
                />
              )}
              {card.cta_text && card.cta_url && (
                <a
                  href={safeHref(card.cta_url)}
                  className="mt-2 inline-block text-sm font-medium hover:underline"
                  style={{ color: "var(--nos-color-primary, #3b82f6)" }}
                >
                  {card.cta_text} →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

/** Overlap: image-backed cards with a bottom scrim and overlaid white copy. */
export function CardsOverlap({ content }: BlockComponentProps) {
  const { heading, subheading, items, columns } = parseCardsContent(content)
  if (items.length === 0) return null
  const gridCols = columns === 3 ? "@sm:grid-cols-2 @lg:grid-cols-3" : "@sm:grid-cols-2"
  return (
    <section className="px-4 @sm:px-6 @md:px-8" style={{ paddingTop: "calc(var(--nos-space-section-y, 64px) * 0.75)", paddingBottom: "calc(var(--nos-space-section-y, 64px) * 0.75)" }}>
      <CardsHeader heading={heading} subheading={subheading} />
      <div className={`mx-auto mt-10 grid max-w-5xl grid-cols-1 gap-4 ${gridCols}`}>
        {items.map((card, i) => (
          <div
            key={i}
            className="relative h-72 overflow-hidden"
            style={{ borderRadius: "var(--nos-radius, 12px)", boxShadow: "var(--nos-elevation-shadow, none)" }}
          >
            {card.image_url ? (
              <img src={card.image_url} alt={card.title ?? ""} className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0" style={{ background: GRADIENT }} aria-hidden />
            )}
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 60%)" }}
              aria-hidden
            />
            <div className="absolute inset-x-0 bottom-0 p-5 text-left">
              {card.title && <h3 className="text-lg font-semibold text-white">{card.title}</h3>}
              {card.body && (
                <p className="mt-1 text-sm leading-relaxed text-white/90" dangerouslySetInnerHTML={{ __html: sanitizeHtml(card.body) }} />
              )}
              {card.cta_text && card.cta_url && (
                <a
                  href={safeHref(card.cta_url)}
                  className="mt-3 inline-block text-sm font-medium text-white underline underline-offset-4 hover:opacity-80"
                >
                  {card.cta_text} →
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
